import type { FloorOutlineMode, GeoJSONPolygon } from "@sitescene/types";

import { normalizeGeoJsonPolygon } from "@sitescene/shared";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Accordion } from "@/components/accordions/accordion";
import { Button } from "@/components/ui/buttons/button";
import { cx } from "@/utils/cx";

import type { LocationManagementState } from "../../../hooks/useLocationManagementState";
import type { ZoneBoundaryDrawConfig } from "../../../map/draw/useMapboxDraw";

import {
	buildAutoFloorOutline,
	FLOOR_EDIT_FILL_OPACITY,
	floorBoundaryFeatureId,
	type FloorOutlineAppearance,
	resolveFloorOutlineAppearance,
	resolveFloorOutlineMode,
} from "../../../map/floors/floor-boundary-overlays";
import { getRefId } from "../../../utils/location/installation-ids";
import {
	filterLocationMapZonesBySearch,
	sortFloorsForList,
} from "../../../utils/location/location-map-utils";
import { toTitleCase } from "../../../utils/shared/strings";
import { SensorAutoSaveStatus, ZoneAutoSaveStatus } from "./auto-save-status";
import { FloorAccordionTitle } from "./floor-accordion-title";
import { FloorReorderList } from "./floor-reorder-list";
import { InlineFloorEditForm } from "./inline-floor-edit-form";
import { SensorEditForm } from "./sensor-edit-form";
import { ZoneAccordionList } from "./zone-accordion-list";
import { ZoneEditForm } from "./zone-edit-form";

interface LocationMapSidebarProps {
	/** Set when the map asks this sidebar to open a floor editor. */
	floorEditRequest?: null | { floorId: string; requestId: number };
	hasInstallation?: boolean;
	isReorderingFloors?: boolean;
	isSidebarOpen?: boolean;
	mapState: LocationManagementState;
	/** Current main-map viewport center for sensor “Use map center”. */
	mapViewportCenter?: [number, number] | null;
	onAddInstallation?: () => void;
	onAddSensor?: (zoneId: string) => void;
	onAddZone?: () => void;
	onFloorBoundaryDrawChange?: (
		config: undefined | ZoneBoundaryDrawConfig,
	) => void;
	onReorderModeChange?: (isReordering: boolean) => void;
	searchQuery?: string;
}

const EDIT_CARD_CLASS_NAME =
	"border-brand bg-secondary_subtle flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border-2";

/**
 * Zone list with the sensor edit form pinned at the top so it stays in view
 * while the admin scrolls through zones.
 */
export const LocationMapSidebar = ({
	floorEditRequest = null,
	hasInstallation = true,
	isReorderingFloors = false,
	isSidebarOpen = true,
	mapState,
	mapViewportCenter = null,
	onAddInstallation,
	onAddSensor,
	onAddZone,
	onFloorBoundaryDrawChange,
	onReorderModeChange,
	searchQuery = "",
}: LocationMapSidebarProps) => {
	const {
		autoSaveStatus,
		canUndoSensorEdit,
		canUndoZoneEdit,
		collapseAllZonePanels,
		editDraft,
		editingSensorId,
		editingZoneId,
		editTypeOptions,
		editZoneOptions,
		handleCancelSensorEdit,
		handleCancelZoneEdit,
		handleEditZoneChange,
		handleExpandZoneBoundaryToIncludeOutsideSensors,
		handleSaveSensorEdit,
		handleSaveZoneEdit,
		handleUndoSensorEdit,
		handleUndoZoneEdit,
		hoverZoneSensorOnMap,
		isEditDirty,
		isUpdatedSensorPending,
		isUpdatedZonePending,
		isZoneEditDirty,
		location,
		moveSensorsWithOutline,
		previewZoneSensorOnMap,
		reorderFloors,
		sensorsOutsideManualBoundary,
		setFloorEditActive,
		setMoveSensorsWithOutline,
		updateEditDraft,
		updateZoneEditDraft,
		zoneAutoSaveStatus,
		zoneEditDraft,
		zoneMoveHint,
		zones,
	} = mapState;

	const floors = useMemo(
		() => sortFloorsForList(location.floors ?? []),
		[location.floors],
	);

	const isEditingSensor = Boolean(editingSensorId && editDraft);
	const isEditingZone = Boolean(editingZoneId && zoneEditDraft);
	useEffect(() => {
		if (!isReorderingFloors) return;
		collapseAllZonePanels();
	}, [collapseAllZonePanels, isReorderingFloors]);

	const handleExitReorderMode = useCallback(() => {
		onReorderModeChange?.(false);
	}, [onReorderModeChange]);

	const editDraftZoneId = editDraft?.zoneId;
	const pinnedZoneIds = useMemo(() => {
		const next = new Set<string>();
		if (editingZoneId) next.add(editingZoneId);
		if (editDraftZoneId) next.add(editDraftZoneId);
		return next;
	}, [editDraftZoneId, editingZoneId]);

	const filteredZoneEntries = useMemo(
		() => filterLocationMapZonesBySearch(zones, searchQuery, pinnedZoneIds),
		[pinnedZoneIds, searchQuery, zones],
	);
	const allZoneEntries = useMemo(
		() => filterLocationMapZonesBySearch(zones, "", pinnedZoneIds),
		[pinnedZoneIds, zones],
	);

	const [editingFloorId, setEditingFloorId] = useState<null | string>(null);
	const [openFloorIds, setOpenFloorIds] = useState<Set<string>>(
		() => new Set(),
	);
	const [sidebarLocationId, setSidebarLocationId] = useState(location.id);
	if (location.id !== sidebarLocationId) {
		setSidebarLocationId(location.id);
		setEditingFloorId(null);
	}
	const focusedZoneFloorId = useMemo(() => {
		if (mapState.openPanels.size !== 1) return null;
		const [index] = mapState.openPanels;
		if (index === undefined) return null;
		return getRefId(zones[index]?.floor) ?? null;
	}, [mapState.openPanels, zones]);
	const floorIdToOpen =
		editingFloorId ?? focusedZoneFloorId ?? mapState.highlightedFloorId;
	const floorExists =
		floorIdToOpen != null && floors.some((floor) => floor.id === floorIdToOpen);
	const floorOpenKey = floorExists ? floorIdToOpen : "";
	const [appliedFloorOpenKey, setAppliedFloorOpenKey] = useState<null | string>(
		null,
	);
	if (floorOpenKey !== appliedFloorOpenKey) {
		setAppliedFloorOpenKey(floorOpenKey);
		if (!floorOpenKey) {
			setOpenFloorIds((current) => (current.size === 0 ? current : new Set()));
		} else {
			setOpenFloorIds((current) =>
				current.size === 1 && current.has(floorOpenKey)
					? current
					: new Set([floorOpenKey]),
			);
		}
	}
	const editingFloor =
		floors.find((floor) => floor.id === editingFloorId) ?? null;
	if ((editingZoneId || editingSensorId) && editingFloorId) {
		setEditingFloorId(null);
	}
	const [appliedFloorEditRequestId, setAppliedFloorEditRequestId] = useState(0);
	if (
		floorEditRequest &&
		floorEditRequest.requestId !== appliedFloorEditRequestId &&
		!editingZoneId &&
		!editingSensorId &&
		floors.some((floor) => floor.id === floorEditRequest.floorId)
	) {
		setAppliedFloorEditRequestId(floorEditRequest.requestId);
		setEditingFloorId(floorEditRequest.floorId);
		setOpenFloorIds(new Set([floorEditRequest.floorId]));
	}
	const isEditingFloor =
		Boolean(editingFloor) && !editingZoneId && !editingSensorId;
	useEffect(() => {
		setFloorEditActive(isEditingFloor);
	}, [isEditingFloor, setFloorEditActive]);
	useEffect(() => () => setFloorEditActive(false), [setFloorEditActive]);
	const isPinnedEditActive = isEditingSensor || isEditingZone || isEditingFloor;
	const savedOutlineMode = resolveFloorOutlineMode(editingFloor);
	const savedBoundary = editingFloor?.boundary;
	const savedCustomOutline = useMemo(
		() => normalizeGeoJsonPolygon(savedBoundary),
		[savedBoundary],
	);
	const [outlineModeDraft, setOutlineModeDraft] = useState<
		FloorOutlineMode | undefined
	>(undefined);
	const [floorOutlineDraft, setFloorOutlineDraft] = useState<
		GeoJSONPolygon | null | undefined
	>(undefined);
	const [floorOutlineFloorId, setFloorOutlineFloorId] = useState<null | string>(
		editingFloorId,
	);
	const [autoOutlineResetKey, setAutoOutlineResetKey] = useState(0);
	const [appearanceDraft, setAppearanceDraft] = useState<
		FloorOutlineAppearance | undefined
	>(undefined);
	if (editingFloorId !== floorOutlineFloorId) {
		setFloorOutlineFloorId(editingFloorId);
		setOutlineModeDraft(undefined);
		setFloorOutlineDraft(undefined);
		setAppearanceDraft(undefined);
		setAutoOutlineResetKey(0);
	}
	const outlineMode = outlineModeDraft ?? savedOutlineMode;
	const floorOutline =
		floorOutlineDraft !== undefined ? floorOutlineDraft : savedCustomOutline;
	const savedBorderWidth = editingFloor?.borderWidth;
	const savedColor = editingFloor?.color;
	const savedFill = editingFloor?.fill;
	const appearance = useMemo(
		() =>
			appearanceDraft ??
			resolveFloorOutlineAppearance({
				borderWidth: savedBorderWidth,
				color: savedColor,
				fill: savedFill,
			}),
		[appearanceDraft, savedBorderWidth, savedColor, savedFill],
	);
	const handleAppearanceChange = useCallback(
		(next: FloorOutlineAppearance) => {
			setAppearanceDraft((current) => {
				const previous =
					current ??
					resolveFloorOutlineAppearance({
						borderWidth: savedBorderWidth,
						color: savedColor,
						fill: savedFill,
					});
				if (
					previous.borderWidth === next.borderWidth &&
					previous.color === next.color &&
					previous.fill === next.fill
				) {
					return current;
				}
				return next;
			});
		},
		[savedBorderWidth, savedColor, savedFill],
	);
	const autoFloorOutline = useMemo(() => {
		if (!editingFloor || outlineMode !== "auto") return null;
		const outline = buildAutoFloorOutline(editingFloor.id, zones);
		if (!outline || autoOutlineResetKey === 0) return outline;
		return {
			coordinates: outline.coordinates.map((ring) =>
				ring.map((position) => [position[0], position[1]] as [number, number]),
			),
			type: "Polygon" as const,
		};
	}, [autoOutlineResetKey, editingFloor, outlineMode, zones]);
	const handleFloorOutlineChange = useCallback(
		(boundary: GeoJSONPolygon | null) => {
			setFloorOutlineDraft(boundary);
		},
		[],
	);
	const handleOutlineModeChange = useCallback(
		(mode: FloorOutlineMode) => {
			if (mode === "auto") {
				setFloorOutlineDraft(undefined);
			} else if (
				mode === "custom" &&
				outlineMode === "auto" &&
				autoFloorOutline
			) {
				// Keep the auto hull so Custom starts editable instead of blank.
				setFloorOutlineDraft({
					coordinates: autoFloorOutline.coordinates.map((ring) =>
						ring.map(
							(position) => [position[0], position[1]] as [number, number],
						),
					),
					type: "Polygon",
				});
			}
			setOutlineModeDraft(mode);
		},
		[autoFloorOutline, outlineMode],
	);
	const floorBoundaryDraw = useMemo((): undefined | ZoneBoundaryDrawConfig => {
		if (!isEditingFloor || !editingFloor || outlineMode === "image") {
			return undefined;
		}

		if (outlineMode === "auto") {
			if (!autoFloorOutline) return undefined;
			return {
				boundary: autoFloorOutline,
				color: appearance.color,
				enabled: true,
				featureId: floorBoundaryFeatureId(editingFloor.id),
				fillOpacity: appearance.fill ? FLOOR_EDIT_FILL_OPACITY : 0,
				geometryType: "polygon",
				lineWidth: appearance.borderWidth,
				onChange: (boundary, action) => {
					if (action === "change_coordinates" && boundary?.type === "Polygon") {
						setOutlineModeDraft("custom");
						setFloorOutlineDraft(boundary);
						return;
					}
					if (action === "move") {
						setAutoOutlineResetKey((current) => current + 1);
					}
				},
				translateOnly: true,
			};
		}

		return {
			boundary: floorOutline,
			color: appearance.color,
			enabled: true,
			featureId: floorBoundaryFeatureId(editingFloor.id),
			fillOpacity: appearance.fill ? FLOOR_EDIT_FILL_OPACITY : 0,
			geometryType: "polygon",
			lineWidth: appearance.borderWidth,
			onChange: (boundary) => {
				if (boundary?.type === "Polygon") {
					handleFloorOutlineChange(boundary);
					return;
				}
				if (boundary === null) handleFloorOutlineChange(null);
			},
		};
	}, [
		autoFloorOutline,
		appearance,
		editingFloor,
		floorOutline,
		handleFloorOutlineChange,
		isEditingFloor,
		outlineMode,
	]);

	useEffect(() => {
		onFloorBoundaryDrawChange?.(floorBoundaryDraw);
	}, [floorBoundaryDraw, onFloorBoundaryDrawChange]);

	useEffect(
		() => () => {
			onFloorBoundaryDrawChange?.(undefined);
		},
		[onFloorBoundaryDrawChange],
	);

	const floorNameMatches = useCallback(
		(name: string) => {
			const query = searchQuery.toLowerCase().trim();
			if (!query) return true;
			return (
				name.toLowerCase().includes(query) ||
				toTitleCase(name).toLowerCase().includes(query)
			);
		},
		[searchQuery],
	);

	const entriesForFloor = useCallback(
		(floorId: string, floorName: string) => {
			const source =
				!searchQuery.trim() || floorNameMatches(floorName)
					? allZoneEntries
					: filteredZoneEntries;
			return source.filter((entry) => getRefId(entry.zone.floor) === floorId);
		},
		[allZoneEntries, filteredZoneEntries, floorNameMatches, searchQuery],
	);

	const visibleFloors = useMemo(
		() =>
			floors.filter((floor) => {
				if (!searchQuery.trim()) return true;
				if (floorNameMatches(floor.name)) return true;
				return entriesForFloor(floor.id, floor.name).length > 0;
			}),
		[entriesForFloor, floorNameMatches, floors, searchQuery],
	);

	const floorIds = useMemo(
		() => new Set(floors.map((floor) => floor.id)),
		[floors],
	);
	const unassignedZoneEntries = useMemo(
		() =>
			filteredZoneEntries.filter((entry) => {
				const floorId = getRefId(entry.zone.floor);
				return !floorId || !floorIds.has(floorId);
			}),
		[filteredZoneEntries, floorIds],
	);

	const floorOpenPanels = useMemo(() => {
		const next = new Set<number>();
		const openId = editingFloorId ?? [...openFloorIds][0];
		const index = visibleFloors.findIndex((floor) => floor.id === openId);
		if (index >= 0) next.add(index);
		return next;
	}, [editingFloorId, openFloorIds, visibleFloors]);

	const handleFloorOpenPanelsChange = useCallback(
		(nextPanels: Set<number>) => {
			const openedIndex = [...nextPanels].find(
				(index) => !floorOpenPanels.has(index),
			);
			if (openedIndex !== undefined) {
				const floorId = visibleFloors[openedIndex]?.id;
				if (!floorId) return;
				mapState.selectFloor(floorId);
				setOpenFloorIds(new Set([floorId]));
				return;
			}

			const closedIndex = [...floorOpenPanels].find(
				(index) => !nextPanels.has(index),
			);
			if (closedIndex !== undefined && visibleFloors[closedIndex]?.id) {
				setOpenFloorIds(new Set());
			}
		},
		[floorOpenPanels, mapState, visibleFloors],
	);

	const floorAccordionItems = useMemo(
		() =>
			visibleFloors.map((floor, index) => {
				const floorZones = entriesForFloor(floor.id, floor.name);
				const isFloorOpen = floorOpenPanels.has(index);
				const isEditingThisFloor = editingFloorId === floor.id;
				const isHighlighted = mapState.highlightedFloorId === floor.id;

				return {
					content: (
						<div className="bg-secondary p-5">
							<div className="rounded-xl shadow-sm">
								<div className="overflow-hidden rounded-xl bg-primary">
									<p className="border-b border-secondary p-4 text-sm font-medium text-primary">
										Zones
									</p>
									{floorZones.length === 0 ? (
										<div className="flex items-center justify-between gap-3 bg-primary px-4 py-3">
											<p className="text-sm text-tertiary">
												No zones on this floor.
											</p>
											{onAddZone ? (
												<Button
													color="link-color"
													onClick={onAddZone}
													size="sm"
												>
													Add Zone
												</Button>
											) : null}
										</div>
									) : (
										<ZoneAccordionList
											entries={floorZones}
											isSidebarOpen={isSidebarOpen}
											mapState={mapState}
											nested
											onAddSensor={onAddSensor}
											onAddZone={onAddZone}
											searchQuery={searchQuery}
										/>
									)}
								</div>
							</div>
						</div>
					),
					headerClassName: cx(
						"border-b-1 border-l-2 px-4 py-2 transition-colors",
						isFloorOpen || isEditingThisFloor || isHighlighted
							? "border-b-secondary border-l-brand-500"
							: "border-transparent",
					),
					id: floor.id,
					title: (
						<FloorAccordionTitle
							floor={floor}
							isActive={isHighlighted || isEditingThisFloor}
							isEditDisabled={isEditingZone || isEditingSensor}
							isEditing={isEditingThisFloor}
							isExpanded={isFloorOpen}
							onAddZone={onAddZone}
							onEdit={() => {
								setEditingFloorId(floor.id);
								mapState.selectFloor(floor.id);
								setOpenFloorIds(new Set([floor.id]));
							}}
							onToggle={() => {
								const isOnlyOpen =
									openFloorIds.size === 1 && openFloorIds.has(floor.id);
								if (isOnlyOpen) {
									setOpenFloorIds(new Set());
									return;
								}

								mapState.selectFloor(floor.id);
								setOpenFloorIds(new Set([floor.id]));
							}}
							zoneCount={floorZones.length}
						/>
					),
				};
			}),
		[
			editingFloorId,
			entriesForFloor,
			floorOpenPanels,
			isEditingSensor,
			isEditingZone,
			isSidebarOpen,
			mapState,
			onAddSensor,
			onAddZone,
			openFloorIds,
			searchQuery,
			visibleFloors,
		],
	);

	const hasVisibleMapRows =
		visibleFloors.length > 0 || unassignedZoneEntries.length > 0;

	return (
		<div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden px-4 pb-6 md:px-6">
			{isEditingZone && zoneEditDraft ? (
				<div className={EDIT_CARD_CLASS_NAME}>
					<div className="flex shrink-0 items-center justify-between gap-2 px-4 pt-4 pb-3">
						<p className="text-sm font-semibold text-primary">Editing zone</p>
						<div className="flex min-h-8 min-w-28 shrink-0 items-center justify-end">
							<ZoneAutoSaveStatus
								autoSaveStatus={zoneAutoSaveStatus}
								canUndoZoneEdit={canUndoZoneEdit}
								isEditDirty={isZoneEditDirty}
								isUpdatedZonePending={isUpdatedZonePending}
								onUndo={handleUndoZoneEdit}
							/>
						</div>
					</div>

					<ZoneEditForm
						autoSaveStatus={zoneAutoSaveStatus}
						canUndoZoneEdit={canUndoZoneEdit}
						editDraft={zoneEditDraft}
						floors={location.floors}
						installationId={getRefId(
							zones.find((zone) => zone.id === editingZoneId)?.installation,
						)}
						isEditDirty={isZoneEditDirty}
						isUpdatedZonePending={isUpdatedZonePending}
						moveSensorsWithOutline={moveSensorsWithOutline}
						onCancel={handleCancelZoneEdit}
						onDraftChange={updateZoneEditDraft}
						onExpandBoundaryToIncludeSensors={
							handleExpandZoneBoundaryToIncludeOutsideSensors
						}
						onHoverOutsideSensor={hoverZoneSensorOnMap}
						onMoveSensorsWithOutlineChange={setMoveSensorsWithOutline}
						onPreviewOutsideSensor={previewZoneSensorOnMap}
						onSave={handleSaveZoneEdit}
						sensorsOutsideBoundary={sensorsOutsideManualBoundary}
					/>
				</div>
			) : null}

			{isEditingSensor && editDraft ? (
				<div className={EDIT_CARD_CLASS_NAME}>
					<div className="flex shrink-0 items-center justify-between gap-2 px-4 py-4 md:px-6">
						<p className="text-sm font-semibold text-primary">Editing sensor</p>
						<div className="flex min-h-8 min-w-28 shrink-0 items-center justify-end">
							<SensorAutoSaveStatus
								autoSaveStatus={autoSaveStatus}
								canUndoSensorEdit={canUndoSensorEdit}
								isEditDirty={isEditDirty}
								isUpdatedSensorPending={isUpdatedSensorPending}
								onUndo={handleUndoSensorEdit}
							/>
						</div>
					</div>

					<SensorEditForm
						autoSaveStatus={autoSaveStatus}
						canUndoSensorEdit={canUndoSensorEdit}
						editDraft={editDraft}
						editTypeOptions={editTypeOptions}
						editZoneOptions={editZoneOptions}
						isEditDirty={isEditDirty}
						isUpdatedSensorPending={isUpdatedSensorPending}
						mapViewportCenter={mapViewportCenter}
						onCancel={handleCancelSensorEdit}
						onDraftChange={updateEditDraft}
						onSave={handleSaveSensorEdit}
						onZoneChange={handleEditZoneChange}
						zoneMoveHint={zoneMoveHint}
					/>
				</div>
			) : null}

			{isEditingFloor && editingFloor ? (
				<div className={EDIT_CARD_CLASS_NAME}>
					<div className="flex shrink-0 items-center justify-between gap-2 px-4 pt-4 pb-3">
						<p className="text-sm font-semibold text-primary">Editing floor</p>
					</div>
					<InlineFloorEditForm
						appearance={appearance}
						boundary={outlineMode === "custom" ? floorOutline : null}
						floor={editingFloor}
						hasAutoOutline={Boolean(autoFloorOutline)}
						onAppearanceChange={handleAppearanceChange}
						onBoundaryChange={handleFloorOutlineChange}
						onClose={() => setEditingFloorId(null)}
						onOutlineModeChange={handleOutlineModeChange}
						outlineMode={outlineMode}
					/>
				</div>
			) : null}

			<div
				className={cx(
					"scrollbar-hide flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto [overflow-anchor:none]",
					isPinnedEditActive && "hidden",
				)}
				data-accordion-scroll-container="true"
			>
				{isReorderingFloors ? (
					<div className="sticky top-0 z-10 flex shrink-0 flex-col justify-end gap-2 border-b border-secondary bg-primary pb-3">
						<p className="mr-auto text-xs text-tertiary">
							Drag floors — top is the first zoom level
						</p>
						<Button color="primary" onClick={handleExitReorderMode} size="sm">
							Done
						</Button>
					</div>
				) : null}

				{isReorderingFloors ? (
					<div className="py-3">
						<FloorReorderList floors={floors} onReorder={reorderFloors} />
					</div>
				) : !hasInstallation ? (
					<div className="flex flex-col items-start gap-3">
						{onAddInstallation ? (
							<Button color="primary" onClick={onAddInstallation} size="sm">
								Add installation
							</Button>
						) : (
							<p className="text-sm text-tertiary">
								No installation on this location yet.
							</p>
						)}
					</div>
				) : !hasVisibleMapRows ? (
					<div className="flex flex-col items-start gap-3">
						<p className="text-sm text-tertiary">
							{searchQuery.trim()
								? "No floors, zones, or sensors match your search."
								: zones.length === 0 && floors.length === 0
									? "No zones yet. Add a zone to start placing sensors."
									: "No floors, zones, or sensors match your search."}
						</p>
						{!searchQuery.trim() &&
						zones.length === 0 &&
						floors.length === 0 &&
						onAddZone ? (
							<Button color="primary" onClick={onAddZone} size="sm">
								Add Zone
							</Button>
						) : null}
					</div>
				) : floors.length > 0 ? (
					<div className="flex flex-col">
						{visibleFloors.length > 0 ? (
							<Accordion
								buttonClassName="py-6"
								className="gap-0"
								items={floorAccordionItems}
								onOpenPanelsChange={handleFloorOpenPanelsChange}
								openPanels={floorOpenPanels}
								scrollOpenedPanelIntoView
								transition={{ duration: 0.25, ease: "easeOut", type: "tween" }}
							/>
						) : null}
						{unassignedZoneEntries.length > 0 ? (
							<div className="mt-4">
								<p className="mb-1 text-xs font-medium text-tertiary">
									No floor
								</p>
								<ZoneAccordionList
									entries={unassignedZoneEntries}
									isSidebarOpen={isSidebarOpen}
									mapState={mapState}
									onAddSensor={onAddSensor}
									onAddZone={onAddZone}
									searchQuery={searchQuery}
								/>
							</div>
						) : null}
					</div>
				) : (
					<ZoneAccordionList
						entries={filteredZoneEntries}
						isSidebarOpen={isSidebarOpen}
						mapState={mapState}
						onAddSensor={onAddSensor}
						onAddZone={onAddZone}
						searchQuery={searchQuery}
					/>
				)}
			</div>
		</div>
	);
};
